<!-- eslint-disable vue/v-on-event-hyphenation -->
<!-- eslint-disable vue/attribute-hyphenation -->
<template>
  <!-- NOTE: Carbon Design System requires 'modelValue' prop name (not 'model-value') -->
  <cv-dropdown
    v-bind="$attrs"
    class="dropdown-component"
    :aria-label="ariaLabel"
    :class="cssClasses"
    :modelValue="value"
    @update:modelValue="updateValue"
    @keydown.esc="handleDropdownEsc"
    @keydown.tab="handleDropdownTab"
  >
    <template v-for="slot in Object.keys(slots)" #[slot]="slotData">
      <slot :name="slot" v-bind="slotData || {}" />
    </template>
  </cv-dropdown>
</template>

<script setup lang="ts">
import { computed, useSlots } from "vue";

// Props
interface Props {
  ariaLabel?: string;
  kind?: string;
  value?: string | null;
}

const { ariaLabel = null, kind = "grey", value = null } = defineProps<Props>();

// Emits
const emit = defineEmits<{
  change: [data: string];
  "update:modelValue": [data: unknown];
}>();

// Get slots
const slots = useSlots();

// Computed
const cssClasses = computed(() => {
  return {
    [`dropdown-${kind}`]: true,
  };
});

// Methods
const handleDropdownEsc = (evt: KeyboardEvent) => {
  const element = (evt.currentTarget as HTMLElement)?.querySelector("button");
  if (element) {
    evt.preventDefault();
    element.focus();
  }
};

const handleDropdownTab = (evt: KeyboardEvent) => {
  const element = (evt.currentTarget as HTMLElement)?.querySelector("button");
  if (element && element !== document.activeElement) {
    evt.preventDefault();
    element.focus();
  }
};

const updateValue = (newValue) => {
  emit("change", newValue);
  emit("update:modelValue", newValue);
};
</script>

<style lang="scss">
@import "@styles/themes/sls-theme/tokens/typo";
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";

.bx--dropdown__wrapper {
  ~ .bx--form-requirement {
    padding-top: $spacing-03;
  }
}

.bx--dropdown {
  a {
    &:focus-visible {
      background-color: $hover-ui;
    }
  }
  &.bx--list-box {
    border: 1px solid $ui-03;
    border-radius: 10px;
    background-color: $ui-background;
  }

  &:active {
    border-color: $focus;
  }

  &:hover {
    background-color: $hover-ui;
  }

  &.dropdown-gray {
    background-color: $ui-background;

    &:hover {
      background-color: $hover-ui;
    }
  }

  &.dropdown-white {
    background-color: $ui-01;

    &:hover {
      background-color: $hover-ui;
    }

    // Override Carbon's default styling for dropdowns nested in a modal
    .bx--modal & {
      background-color: $ui-01;

      &:hover {
        background-color: $hover-ui;
      }
    }
  }

  &.bx--list-box--expanded {
    &:hover {
      background-color: $hover-ui;
    }
  }

  &.bx--dropdown--inline {
    border: none;
    max-height: 2rem;
  }

  &.bx--dropdown--invalid {
    .bx--list-box__invalid-icon {
      display: none;
    }
  }

  &.bx--dropdown--up {
    > .bx--list-box__menu {
      bottom: 2.5rem;
    }
  }

  > .bx--list-box__field {
    @include carbon--type-style("body-short-01", true);
    border: 1px solid transparent;
    border-radius: 10px;

    &:focus {
      outline: none;
    }
  }

  .bx--dropdown-text {
    cursor: pointer;
    @include carbon--type-style("productive-heading-01", true);
  }

  .bx--dropdown__arrow {
    fill: $interactive-01;
  }

  .bx--list-box__menu {
    border-radius: 10px;
    box-shadow: 0 2px 6px #0003;

    &:focus {
      outline: transparent;
    }
  }

  .bx--list-box__menu-icon {
    width: 2.5rem;
    height: 2.5rem;
    right: 0;
    padding: $spacing-03;
    border-left: 1px solid $ui-02;

    > svg {
      width: 24px;
      height: 24px;
    }
  }

  .bx--list-box__menu-icon--open {
    border-left: none;
    border-right: 1px solid $ui-02;
  }

  &.bx--dropdown--disabled {
    background-color: $disabled-01;

    &:hover {
      background-color: $disabled-01;
    }

    .bx--modal & {
      background-color: $disabled-01;

      &:hover {
        background-color: $disabled-01;
      }
    }

    .bx--list-box__label {
      color: $disabled-02;
    }

    .bx--list-box__field,
    .bx--list-box__menu-icon {
      cursor: not-allowed;
      opacity: 0.5;
    }

    .bx--list-box__field {
      // to overwrite browser default opacity: 0.5;
      opacity: 1;
    }

    .bx--list-box__menu-icon {
      &::before {
        filter: none;
      }
    }
  }

  .bx--dropdown-link,
  .bx--list-box__label {
    // Display flex for alignment of text in dropdown for nested span that is using
    // ellipsis text which has a display of inline-block
    display: flex;
    align-items: center;

    @include carbon--type-style("body-short-01", true);

    &:focus {
      outline: transparent;
      border: 1px solid $focus;
    }
  }
}

.bx--dropdown-link {
  border-radius: inherit;
}

.bx--dropdown-item {
  transform: translateZ(0);

  &:first-child {
    border-radius: 10px 10px 0 0;
  }

  &:last-child {
    border-radius: 0 0 10px 10px;
  }

  &:only-child {
    border-radius: 10px;
  }

  &[disabled="true"] {
    cursor: not-allowed;

    .bx--dropdown-link {
      pointer-events: none;
      color: $disabled-01;
    }
  }

  &.dropdown-sub-item {
    .bx--dropdown-link {
      padding-left: $spacing-06;

      // margin is removed when focus, increased value to cater the margin differences
      &:focus {
        padding-left: $spacing-08;
      }
    }
  }
}

// customisation to support dropdown item grouping
.bx--dropdown-item[dropdown-item-label] {
  cursor: auto;
  pointer-events: none;

  .bx--dropdown-link {
    cursor: auto;
    pointer-events: none;
    @include carbon--type-style("label-03", true);
    color: $text-03;
    margin-left: $spacing-04;
    text-transform: uppercase;
  }
}
</style>
